import type { FC, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectedCardSelector } from 'store/slices/userCard';

import { ReservationCardStatus } from '../types';
import { isReservationFlowActive } from '../utils/reservationFlags';
import { getReservationEntryPath, isReservationHoldMarked } from '../utils/reservationHold';

interface ReservationHoldGateProps {
  children: ReactNode;
}

/**
 * Wraps the non-final deposit status routes (processing / await).
 *
 * Once a hold has been handed over to the reservation flow, any later navigation to those pages —
 * the submit response, a 3DS child frame, `Await` timing out — is stale: the player belongs on the
 * reservation screens. Bouncing here, before `StatusController` mounts, also keeps its session-state
 * request, hub subscription and redirect timer from starting at all.
 *
 * Final pages (success / error) are deliberately not gated: a real result must always be shown.
 */
export const ReservationHoldGate: FC<ReservationHoldGateProps> = ({ children }) => {
  const isActive = useAppSelector(isReservationFlowActive);
  const { selectedCard } = useAppSelector(selectedCardSelector);
  const isCardVerified =
    (selectedCard?.kyc_verification_status as ReservationCardStatus | null) === ReservationCardStatus.verified;

  if (isActive && !isCardVerified && isReservationHoldMarked()) {
    return <Navigate replace to={getReservationEntryPath()} />;
  }

  return <>{children}</>;
};
